import { useEffect, useState, useRef } from 'react';
import { Turnstile } from '~/components/ui/turnstile';
import { useTheme } from '~/hooks/use-theme';
import {
  hasValidToken,
  initializeAuthToken,
  isTurnstileEnabled,
  setTurnstileCallback,
} from '~/services/auth-service';
import { toast } from 'sonner';
import { cn } from '~/lib/utils';
import { useTranslation } from 'react-i18next';

// Add a log to see when this file is imported
// console.log('[AUTH_INIT_MODULE] Auth initializer module loaded');

export function AuthInitializer() {
  // console.log('[AuthInitializer] Component function called');

  const { theme } = useTheme();
  const { t } = useTranslation();
  const [isRefresh, setIsRefresh] = useState(false);
  // We keep isMounted state for hydration fix, but add a ref for async updates
  const [isMountedState, setIsMountedState] = useState(false);
  const isMountedRef = useRef(false); // Ref to track mount status for async operations

  // console.log('[AuthInitializer] Component initializing', {
  //   turnstileEnabled: isTurnstileEnabled,
  //   hasValidToken: hasValidToken(),
  // });

  // Only show Turnstile if it's enabled and there's no valid token
  // Start with false, only show when getTurnstileToken is called and needed.
  const [needsTurnstile, setNeedsTurnstile] = useState(false);

  const turnstileTokenPromiseRef = useRef<{
    resolve: (token: string) => void;
    reject: (error: Error) => void;
    context?: 'initial' | 'refresh';
  } | null>(null);

  // Safe state setters that check if component is still mounted
  const safeSetNeedsTurnstile = (value: boolean) => {
    if (isMountedRef.current) {
      setNeedsTurnstile(value);
    }
  };

  const safeSetIsRefresh = (value: boolean) => {
    if (isMountedRef.current) {
      setIsRefresh(value);
    }
  };

  // Function to get a Turnstile token via promise
  const getTurnstileToken = (
    context: 'initial' | 'refresh' = 'initial'
  ): Promise<string> => {
    // console.log(
    //   '[AuthInitializer] Getting turnstile token, enabled:',
    //   isTurnstileEnabled,
    //   'context:',
    //   context
    // );

    return new Promise((resolve, reject) => {
      // If this is a refresh request and we have a valid token,
      // return a special marker to indicate we should use the current token
      if (context === 'refresh' && hasValidToken()) {
        // console.log(
        //   '[AuthInitializer] Valid token exists for refresh, bypassing Turnstile'
        // );
        resolve('use-current-token');
        return;
      }

      // If Turnstile is disabled, resolve with a dummy token
      if (!isTurnstileEnabled) {
        // console.log('[AuthInitializer] Turnstile disabled, using dummy token');
        resolve('turnstile-disabled');
        return;
      }

      // If Turnstile is enabled, NOW we set needsTurnstile to true (use safe setter)
      safeSetNeedsTurnstile(true);
      // console.log('[AuthInitializer] Turnstile enabled, showing widget');

      // Determine refresh status based on explicit context
      const callerIsRefresh = context === 'refresh';
      // console.log(
      //   '[AuthInitializer] Turnstile request context indicates refresh:',
      //   callerIsRefresh
      // );
      safeSetIsRefresh(callerIsRefresh); // Use safe setter

      // console.log(
      //   '[AuthInitializer] Turnstile request is for context:',
      //   context
      // );
      turnstileTokenPromiseRef.current = {
        resolve,
        reject,
        context: context,
      };
      // console.log('[AuthInitializer] Showing Turnstile widget');
    });
  };

  // Handle successful Turnstile verification
  const handleTurnstileVerify = (token: string) => {
    // console.log('[AuthInitializer] Turnstile verification successful');
    if (turnstileTokenPromiseRef.current) {
      // console.log('[AuthInitializer] Resolving turnstile token promise');
      turnstileTokenPromiseRef.current.resolve(token);
      turnstileTokenPromiseRef.current = null;
    } else {
      console.warn('[AuthInitializer] No pending token promise to resolve');
    }
    safeSetNeedsTurnstile(false); // Use safe setter
  };

  // Handle Turnstile error
  const handleTurnstileError = (error: string) => {
    console.error('[AuthInitializer] Turnstile error:', error);
    const requestContext = turnstileTokenPromiseRef.current?.context;

    if (turnstileTokenPromiseRef.current) {
      // console.log(
      //   '[AuthInitializer] Rejecting turnstile token promise due to error'
      // );
      turnstileTokenPromiseRef.current.reject(
        new Error(`Turnstile verification failed: ${error}`)
      );
      turnstileTokenPromiseRef.current = null;
    }

    // Only show toast for initial verification errors, not automatic refreshes
    if (requestContext === 'initial') {
      // console.log(
      //   '[AuthInitializer] Showing error toast for failed initial verification'
      // );
      toast.error(t('errors.securityVerificationFailed'));
    }

    safeSetNeedsTurnstile(false); // Use safe setter
  };

  // Handle Turnstile expiration
  const handleTurnstileExpire = () => {
    console.warn('[AuthInitializer] Turnstile verification expired');
    const requestContext = turnstileTokenPromiseRef.current?.context;

    if (turnstileTokenPromiseRef.current) {
      // console.log(
      //   '[AuthInitializer] Rejecting turnstile token promise due to expiration'
      // );
      turnstileTokenPromiseRef.current.reject(
        new Error('Turnstile verification expired')
      );
      turnstileTokenPromiseRef.current = null;
    }

    // Re-trigger turnstile based on the original context
    if (requestContext) {
      // console.log(
      //   `[AuthInitializer] Re-requesting turnstile token after expiration (context: ${requestContext})`
      // );
      getTurnstileToken(requestContext); // Re-request with the same context
    } else {
      // console.log(
      //   '[AuthInitializer] Setting needsTurnstile to true after expiration (unknown context)'
      // );
      // Only set if still mounted
      if (isMountedRef.current) {
        safeSetNeedsTurnstile(true); // Fallback if context unknown - use safe setter
      }
    }
  };

  // Main effect for initialization and setting up mount tracking
  useEffect(() => {
    isMountedRef.current = true;
    // console.log('[AuthInitializer] Component mounted, isMountedRef set to true');

    // Register the turnstile token getter with the auth service
    // console.log(
    //   '[AuthInitializer] Registering turnstile callback with auth service'
    // );
    setTurnstileCallback(getTurnstileToken);

    // Only attempt initialization if we don't already have a valid token
    if (!hasValidToken()) {
      // console.log(
      //   '[AuthInitializer] No valid token, initializing authentication'
      // );

      // Get a Turnstile token first (initial context), then initialize auth
      // console.log('[AuthInitializer] Requesting turnstile token (initial)');
      getTurnstileToken('initial') // Pass 'initial' context here
        .then((token) => {
          // console.log(
          //   '[AuthInitializer] Got turnstile token, initializing auth'
          // );
          return initializeAuthToken(token);
        })
        .then((success: boolean) => {
          // console.log(
          //   '[AuthInitializer] Auth token initialization result:',
          //   success
          // );
          // Check context before showing toast for initialization failure
          const requestContext =
            turnstileTokenPromiseRef.current?.context || 'initial';
          if (!success && requestContext === 'initial') {
            console.warn(
              '[AuthInitializer] Auth initialization failed, showing toast'
            );
            toast.error(t('errors.authInitFailed'));
          }
        })
        .catch((error: Error) => {
          console.error(
            '[AuthInitializer] Failed to initialize auth token:',
            error
          );
          // Hide Turnstile on error only if still mounted
          safeSetNeedsTurnstile(false);
          // Check context before showing toast for initialization failure
          const requestContext =
            turnstileTokenPromiseRef.current?.context || 'initial';
          if (requestContext === 'initial' && isMountedRef.current) {
            // Check mount status for toast too
            console.warn(
              '[AuthInitializer] Showing error toast for auth init failure'
            );
            toast.error(t('errors.authInitFailed'));
          }
        });
    } else {
      // console.log(
      //   '[AuthInitializer] Valid token exists, setting up token refresh'
      // );
      // For token refresh, we use a special marker that indicates we should use the current token
      initializeAuthToken('use-current-token').catch((error: Error) => {
        console.error(
          '[AuthInitializer] Error setting up token refresh:',
          error
        );
      });
    }

    // Cleanup function: Set ref to false and clear promise ref when component unmounts
    return () => {
      isMountedRef.current = false;
      turnstileTokenPromiseRef.current = null; // Clear pending promise ref on unmount
      // console.log('[AuthInitializer] Component unmounting, isMountedRef set to false, promise ref cleared');
    };
  }, [t]); // Ensure all dependencies used in useEffect are listed, like 't' if used.

  // Separate effect for setting the isMounted *state* variable for hydration fix
  useEffect(() => {
    setIsMountedState(true);
  }, []);

  // If Turnstile isn't needed OR component hasn't hydrated/mounted (state check), render nothing
  if (!isMountedState || !needsTurnstile) {
    // console.log('[AuthInitializer] Turnstile not needed or component not hydrated, rendering null');
    return null;
  }

  // console.log('[AuthInitializer] Rendering Turnstile widget', { isRefresh });

  // Render Turnstile widget with a different UI for auto-refresh
  return (
    <div
      className={cn(
        'fixed z-50',
        isRefresh
          ? 'right-4 bottom-4' // Position in corner for refresh
          : 'inset-0 flex items-center justify-center bg-black/50' // Full screen for initial
      )}
      onClick={(e) => {
        // Only stop propagation for the modal background, not the widget itself
        if (e.target === e.currentTarget) {
          // console.log(
          //   '[AuthInitializer] Background click detected, stopping propagation'
          // );
          e.stopPropagation();

          // Allow closing on background click for both initial and refresh states
          // Use context for logging/error message
          const requestContext = turnstileTokenPromiseRef.current?.context;
          if (turnstileTokenPromiseRef.current) {
            // console.log(
            //   `[AuthInitializer] Background clicked (context: ${requestContext}), rejecting promise and hiding Turnstile`
            // );
            turnstileTokenPromiseRef.current.reject(
              new Error(
                `User cancelled via background click (context: ${requestContext})`
              )
            );
            turnstileTokenPromiseRef.current = null;
            setNeedsTurnstile(false);
          }
        } else {
          // console.log(
          //   '[AuthInitializer] Click on widget, allowing propagation'
          // );
        }
      }}
      data-testid="turnstile-container"
    >
      <div
        className={cn(
          'rounded-lg shadow-lg',
          isRefresh ? 'p-3' : 'p-6',
          theme === 'dark' ? 'bg-slate-900' : 'bg-white'
        )}
      >
        {!isRefresh && (
          <div className="mb-4 text-center">
            <h2 className="mb-2 text-lg font-semibold">
              {t('security.checkTitle')}
            </h2>
            <p className="mb-4 text-sm text-gray-500">
              {t('security.checkDescription')}
            </p>
          </div>
        )}

        <Turnstile
          siteKey={import.meta.env.VITE_CLOUDFLARE_TURNSTILE_SITE_KEY || ''}
          onVerify={handleTurnstileVerify}
          onError={handleTurnstileError}
          onExpire={handleTurnstileExpire}
          theme={theme === 'dark' ? 'dark' : 'light'}
          size={isRefresh ? 'compact' : 'normal'}
          className="mx-auto"
        />

        {/* Add Cancel button only for the initial check */}
        {!isRefresh && (
          <button
            onClick={() => {
              if (turnstileTokenPromiseRef.current) {
                const requestContext = turnstileTokenPromiseRef.current.context;
                // console.log(
                //   `[AuthInitializer] Cancel button clicked (context: ${requestContext}), rejecting promise`
                // );
                turnstileTokenPromiseRef.current.reject(
                  new Error(
                    `User cancelled via button (context: ${requestContext})`
                  )
                );
                turnstileTokenPromiseRef.current = null;
                setNeedsTurnstile(false);
              }
            }}
            className={cn(
              'mt-4 w-full rounded bg-gray-200 px-4 py-2 text-sm font-medium',
              'hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600'
            )}
          >
            {t('common.cancel')}
          </button>
        )}

        {isRefresh && (
          <div className="mt-2 text-center text-xs text-gray-500">
            {t('security.sessionRefresh')}
          </div>
        )}
      </div>
    </div>
  );
}
